import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
    UploadCloud, Copy, Check, Edit2, Trash2, X, Search, Download,
    FileVideo, FileImage, FileText, FileAudio, File, Eye, CheckCircle, XCircle
} from 'lucide-react';
import API_URL from '../api';
import { resolveAssetUrl } from '../utils/assetUrl';
import { copyText } from '../utils/clipboard';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from './ConfirmModal';
import './Library.css';

const Library = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [dragActive, setDragActive] = useState(false);
    
    // Multi-upload queue state
    const [uploadQueue, setUploadQueue] = useState([]);
    const uploadQueueRef = useRef([]);
    const uploadingRef = useRef(false);

    // Sync queue ref
    useEffect(() => {
        uploadQueueRef.current = uploadQueue;
    }, [uploadQueue]);

    // Actions states
    const [previewItem, setPreviewItem] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const { success, error: toastError } = useToast();
    const fileInputRef = useRef(null);

    const fetchLibraryItems = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/library`);
            setItems(response.data);
        } catch (err) {
            console.error('Failed to fetch library items:', err);
            toastError('Не вдалося завантажити бібліотеку файлів');
        } finally {
            setLoading(false);
        }
    }, [toastError]);

    useEffect(() => {
        fetchLibraryItems();
    }, [fetchLibraryItems]);

    // Handle Drag & Drop events
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            processFiles(Array.from(e.target.files));
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const processFiles = (rawFiles) => {
        const MAX_SIZE = 500 * 1024 * 1024;
        const validFiles = [];
        let rejectedCount = 0;

        rawFiles.forEach(file => {
            if (file.size > MAX_SIZE) {
                rejectedCount++;
            } else {
                validFiles.push({
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    file,
                    name: file.name,
                    size: file.size,
                    status: 'pending',
                    progress: 0,
                    error: null
                });
            }
        });

        if (rejectedCount > 0) {
            toastError(`${rejectedCount} файл(ів) відхилено — перевищено ліміт 500 МБ`);
        }

        if (validFiles.length > 0) {
            setUploadQueue(prev => {
                const next = [...prev, ...validFiles];
                if (!uploadingRef.current) {
                    uploadingRef.current = true;
                    setTimeout(() => runUploadQueue(), 0);
                }
                return next;
            });
        }
    };

    const runUploadQueue = async () => {
        const queue = uploadQueueRef.current;
        for (let i = 0; i < queue.length; i++) {
            const item = queue[i];
            if (item.status !== 'pending') continue;

            setIsUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            formData.append('file', item.file);
            formData.append('title', item.name);

            try {
                await axios.post(`${API_URL}/library/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(pct);
                        setUploadQueue(prev => prev.map(f =>
                            f.id === item.id ? { ...f, progress: pct } : f
                        ));
                    }
                });
                setUploadQueue(prev => prev.map(f =>
                    f.id === item.id ? { ...f, status: 'done', progress: 100 } : f
                ));
                success(`Файл "${item.name}" завантажено`);
                fetchLibraryItems();
            } catch (err) {
                console.error('Upload error:', err);
                const errMsg = err.response?.data?.error || 'Помилка при завантаженні';
                setUploadQueue(prev => prev.map(f =>
                    f.id === item.id ? { ...f, status: 'error', error: errMsg } : f
                ));
                toastError(`"${item.name}": ${errMsg}`);
            } finally {
                setIsUploading(false);
                setUploadProgress(0);
            }
        }
        uploadingRef.current = false;
    };

    const clearFinishedQueue = () => {
        setUploadQueue([]);
    };

    const startUpload = () => {
        if (uploadingRef.current) return;
        uploadingRef.current = true;
        runUploadQueue();
    };

    const cancelQueue = () => {
        uploadingRef.current = false;
        setUploadQueue([]);
        setIsUploading(false);
        setUploadProgress(0);
    };

    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleRenameStart = (item) => {
        setEditingId(item._id);
        setEditingTitle(item.title);
    };

    const handleRenameSave = async (id) => {
        if (!editingTitle.trim()) {
            toastError('Назва не може бути порожньою');
            return;
        }
        try {
            await axios.put(`${API_URL}/library/${id}`, { title: editingTitle });
            success('Файл перейменовано');
            setEditingId(null);
            fetchLibraryItems();
        } catch (err) {
            console.error('Rename error:', err);
            toastError('Помилка при перейменуванні файлу');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deletingId) return;
        try {
            await axios.delete(`${API_URL}/library/${deletingId}`);
            success('Файл видалено з бібліотеки');
            setDeletingId(null);
            fetchLibraryItems();
        } catch (err) {
            console.error('Delete error:', err);
            toastError('Помилка при видаленні файлу');
        }
    };

    const copyLinkToClipboard = async (url, id) => {
        try {
            await copyText(url);
            setCopiedId(id);
            success('Відносне посилання скопійовано');
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            toastError('Не вдалося скопіювати посилання');
        }
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const renderFileIcon = (type) => {
        switch (type) {
            case 'video':
                return <FileVideo className="fallback-icon text-purple" />;
            case 'image':
                return <FileImage className="fallback-icon text-green" />;
            case 'pdf':
                return <FileText className="fallback-icon text-red" style={{ color: '#ef4444' }} />;
            case 'document':
                return <FileText className="fallback-icon text-yellow" style={{ color: '#eab308' }} />;
            case 'audio':
                return <FileAudio className="fallback-icon text-blue" />;
            default:
                return <File className="fallback-icon" />;
        }
    };

    // Filter items locally
    const filteredItems = items.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                             item.originalName.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTab = activeTab === 'all' || item.type === activeTab;
        return matchesSearch && matchesTab;
    });

    return (
        <div className="library-container">
            <div className="library-header">
                <div>
                    <h1>📚 Бібліотека файлів</h1>
                    <p>Локальне сховище для навчальних відео, лекцій, PDF-інструкцій та зображень</p>
                </div>
            </div>

            {/* Drag & Drop Upload Section */}
            <div 
                className={`upload-section ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                onClick={triggerFileSelect}
            >
                <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="file-input-hidden"
                    multiple
                    accept="image/*,video/*,application/pdf,audio/*,.doc,.docx,.xls,.xlsx,.txt,.csv"
                />
                <div className="upload-icon-wrapper">
                    <UploadCloud />
                </div>
                <h3>{isUploading ? 'Завантаження файлів...' : 'Перетягніть файли сюди або натисніть для вибору'}</h3>
                <p>Дозволено відео, зображення, PDF, аудіо та текстові документи (макс. 500 МБ кожен). Можна вибрати кілька файлів одночасно.</p>

                {isUploading && (
                    <div className="upload-progress-container" onClick={(e) => e.stopPropagation()}>
                        <div className="upload-progress-text">
                            <span>Завантаження...</span>
                            <span>{uploadProgress}%</span>
                        </div>
                        <div className="upload-progress-bar">
                            <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Upload Queue Panel */}
            {uploadQueue.length > 0 && (
                <div className="upload-queue-panel" onClick={(e) => e.stopPropagation()}>
                    <div className="upload-queue-header">
                        <h3>Очікування завантаження ({uploadQueue.filter(f => f.status === 'done').length}/{uploadQueue.length})</h3>
                        <div className="upload-queue-actions">
                            {uploadQueue.some(f => f.status === 'pending') && !isUploading && (
                                <button className="queue-btn queue-btn-start" onClick={startUpload}>
                                    ▶ Завантажити все
                                </button>
                            )}
                            <button className="queue-btn queue-btn-clear" onClick={clearFinishedQueue}>
                                Очистити список
                            </button>
                            {isUploading && (
                                <button className="queue-btn queue-btn-cancel" onClick={cancelQueue}>
                                    ✕ Скасувати
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="upload-queue-list">
                        {uploadQueue.map((item) => (
                            <div key={item.id} className={`upload-queue-item ${item.status}`}>
                                <div className="queue-item-status">
                                    {item.status === 'done' && <CheckCircle size={16} className="queue-icon-done" />}
                                    {item.status === 'error' && <XCircle size={16} className="queue-icon-error" />}
                                    {item.status === 'uploading' && (
                                        <div className="queue-spinner" />
                                    )}
                                    {item.status === 'pending' && <span className="queue-icon-pending">○</span>}
                                </div>
                                <div className="queue-item-info">
                                    <span className="queue-item-name">{item.name}</span>
                                    {item.error && <span className="queue-item-error">{item.error}</span>}
                                </div>
                                <span className="queue-item-size">{formatBytes(item.size)}</span>
                                {item.status === 'uploading' && (
                                    <div className="queue-item-progress">
                                        <div className="queue-item-progress-bar">
                                            <div className="queue-item-progress-fill" style={{ width: `${item.progress}%` }} />
                                        </div>
                                        <span className="queue-item-progress-text">{item.progress}%</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Toolbar Filters & Search */}
            <div className="library-toolbar">
                <div className="filter-tabs">
                    <button 
                        className={`filter-tab ${activeTab === 'all' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('all')}
                    >
                        Усі файли
                    </button>
                    <button 
                        className={`filter-tab ${activeTab === 'video' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('video')}
                    >
                        Відео
                    </button>
                    <button 
                        className={`filter-tab ${activeTab === 'image' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('image')}
                    >
                        Зображення
                    </button>
                    <button 
                        className={`filter-tab ${activeTab === 'pdf' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('pdf')}
                    >
                        PDF
                    </button>
                    <button 
                        className={`filter-tab ${activeTab === 'document' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('document')}
                    >
                        Документи
                    </button>
                    <button 
                        className={`filter-tab ${activeTab === 'audio' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('audio')}
                    >
                        Аудіо
                    </button>
                </div>

                <div className="search-wrapper">
                    <Search className="search-icon" size={18} />
                    <input 
                        type="text" 
                        placeholder="Пошук за назвою..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            {/* Library Grid */}
            {loading ? (
                <div className="empty-library">
                    <h3>Завантаження бібліотеки...</h3>
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="empty-library">
                    <h3>У бібліотеці немає відповідних файлів</h3>
                    <p>Завантажте свій перший навчальний матеріал за допомогою зони завантаження вище.</p>
                </div>
            ) : (
                <div className="library-grid">
                    {filteredItems.map(item => (
                        <div className="library-card" key={item._id}>
                            <div className="library-card-preview">
                                {item.type === 'image' ? (
                                    <img src={resolveAssetUrl(item.url)} alt={item.title} className="library-card-img" />
                                ) : (
                                    <div className="library-card-fallback-preview">
                                        {renderFileIcon(item.type)}
                                    </div>
                                )}
                                
                                <div 
                                    className="library-card-preview-overlay"
                                    onClick={() => setPreviewItem(item)}
                                >
                                    <button className="preview-btn" title="Переглянути">
                                        <Eye size={20} />
                                    </button>
                                </div>
                            </div>

                            <div className="library-card-info">
                                <div className="library-card-title-row">
                                    {editingId === item._id ? (
                                        <div className="rename-input-wrapper">
                                            <input 
                                                type="text" 
                                                value={editingTitle} 
                                                onChange={(e) => setEditingTitle(e.target.value)}
                                                className="rename-input"
                                                autoFocus
                                            />
                                            <button 
                                                onClick={() => handleRenameSave(item._id)}
                                                className="rename-btn-confirm"
                                            >
                                                Зберегти
                                            </button>
                                            <button 
                                                onClick={() => setEditingId(null)}
                                                className="close-modal-btn"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    ) : (
                                        <h4 className="library-card-title" title={item.title}>
                                            {item.title}
                                        </h4>
                                    )}
                                </div>

                                <div className="library-card-meta">
                                    <span className="library-card-type-badge">{item.type}</span>
                                    <span>{formatBytes(item.size)}</span>
                                </div>

                                <div className="library-card-actions">
                                    <button 
                                        onClick={() => copyLinkToClipboard(item.url, item._id)}
                                        className="card-action-btn"
                                        title="Копіювати відносне посилання для курсу"
                                    >
                                        {copiedId === item._id ? <Check size={14} /> : <Copy size={14} />}
                                        <span>Посилання</span>
                                    </button>
                                    <button 
                                        onClick={() => handleRenameStart(item)}
                                        className="card-action-btn"
                                        title="Перейменувати"
                                    >
                                        <Edit2 size={14} />
                                        <span>Назва</span>
                                    </button>
                                    <button 
                                        onClick={() => setDeletingId(item._id)}
                                        className="card-action-btn btn-delete"
                                        title="Видалити"
                                    >
                                        <Trash2 size={14} />
                                        <span>Видалити</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Media Preview Modal */}
            {previewItem && (
                <div className="preview-modal-overlay" onClick={() => setPreviewItem(null)}>
                    <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="preview-modal-header">
                            <h3>{previewItem.title}</h3>
                            <button className="close-modal-btn" onClick={() => setPreviewItem(null)}>
                                <X />
                            </button>
                        </div>
                        <div className="preview-modal-body">
                            {previewItem.type === 'video' && (
                                <video src={resolveAssetUrl(previewItem.url)} controls autoPlay className="preview-media-video" />
                            )}
                            {previewItem.type === 'image' && (
                                <img src={resolveAssetUrl(previewItem.url)} alt={previewItem.title} className="preview-media-img" />
                            )}
                            {previewItem.type === 'pdf' && (
                                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <a
                                        href={resolveAssetUrl(previewItem.url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#38bdf8', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', alignSelf: 'flex-end', padding: '0.35rem 0.75rem', background: 'rgba(56,189,248,0.1)', borderRadius: '6px', border: '1px solid rgba(56,189,248,0.2)' }}
                                    >
                                        <Download size={14} /> Відкрити PDF у новій вкладці
                                    </a>
                                    <object
                                        data={resolveAssetUrl(previewItem.url)}
                                        type="application/pdf"
                                        style={{ width: '100%', height: '60vh', borderRadius: '8px', background: '#1a1a2e' }}
                                    >
                                        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                                            Ваш браузер не підтримує вбудований перегляд PDF.
                                            <br />
                                            <a href={resolveAssetUrl(previewItem.url)} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8' }}>
                                                Завантажити файл
                                            </a>
                                        </p>
                                    </object>
                                </div>
                            )}
                            {previewItem.type === 'audio' && (
                                <audio src={resolveAssetUrl(previewItem.url)} controls autoPlay style={{ width: '100%' }} />
                            )}
                            {!['video', 'image', 'pdf', 'audio'].includes(previewItem.type) && (
                                <div style={{ color: '#fff', textAlign: 'center', padding: '2rem' }}>
                                    <p>Цей тип файлу не підтримує онлайн-перегляд.</p>
                                    <a href={resolveAssetUrl(previewItem.url)} download className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', marginTop: '1rem' }}>
                                        <Download size={16} /> Завантажити
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal 
                isOpen={!!deletingId}
                title="Видалити файл?"
                message="Ви впевнені, що хочете видалити цей файл із бібліотеки? Це також фізично видалить його з сервера і призведе до непрацездатності посилань на цей файл у будь-яких курсах."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeletingId(null)}
                confirmText="Видалити"
                cancelText="Скасувати"
            />
        </div>
    );
};

export default Library;
